/**
 * TaskFormModal (#178) — modal "Nova tarefa" (mockup DS 1.9).
 * Substitui o formulário INLINE que abria dentro do acordeão da fase.
 * Mesmos campos: título, responsável, marco, início/fim, dependência + modo.
 */

import React, { useEffect, useState } from 'react';

import { Button, Checkbox, Input, Modal, ModalContent, ModalFooter, ModalHeader, Select, type SelectOption } from '@/shared/components/ui';

import type { DraftTask } from '../../hooks/useProjectDraft';
import type { DependencyMode } from '../../types';

const MODE_OPTIONS: SelectOption[] = [
  { value: 'inform', label: 'Informar (só notifica ao liberar)' },
  { value: 'warn', label: 'Avisar (confirma antes de avançar)' },
  { value: 'block', label: 'Bloquear (impede avançar antes)' },
];

const today = (): string => new Date().toISOString().slice(0, 10);

export interface TaskFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  phaseName: string;
  responsibleOptions: SelectOption[];
  depOptions: SelectOption[];
  onAdd: (task: Omit<DraftTask, 'localId' | 'phaseId'>) => void;
}

export const TaskFormModal: React.FC<TaskFormModalProps> = ({
  isOpen,
  onClose,
  phaseName,
  responsibleOptions,
  depOptions,
  onAdd,
}) => {
  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [isMilestone, setIsMilestone] = useState(false);
  const [depLocalId, setDepLocalId] = useState('');
  const [depMode, setDepMode] = useState<DependencyMode>('inform');

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setAssigneeId('');
      setStartDate(today());
      setEndDate(today());
      setIsMilestone(false);
      setDepLocalId('');
      setDepMode('inform');
    }
  }, [isOpen]);

  const canAdd = title.trim().length > 0 && !!startDate;

  const handleAdd = (): void => {
    if (!canAdd) return;
    onAdd({
      title: title.trim(),
      ...(assigneeId ? { assigneeId } : {}),
      startDate,
      endDate: isMilestone ? startDate : endDate,
      isMilestone,
      dependencies: depLocalId ? [{ localId: depLocalId, mode: depMode }] : [],
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
      <ModalHeader onClose={onClose}>Nova tarefa · {phaseName}</ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
              Título da tarefa
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título da tarefa"
              data-testid="task-modal-title"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Responsável"
              options={responsibleOptions}
              value={assigneeId}
              onChange={setAssigneeId}
              data-testid="task-modal-assignee"
            />
            <div className="flex items-end">
              <Checkbox
                label="Marco (data única)"
                checked={isMilestone}
                onChange={(checked) => {
                  setIsMilestone(checked);
                  if (checked) setEndDate(startDate);
                }}
                data-testid="task-modal-milestone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Início
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (isMilestone) setEndDate(e.target.value);
                }}
                data-testid="task-modal-start"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Fim
              </label>
              <Input
                type="date"
                value={isMilestone ? startDate : endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isMilestone}
                data-testid="task-modal-end"
              />
            </div>
          </div>

          {depOptions.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                label="Depende de"
                options={[{ value: '', label: 'Nenhuma' }, ...depOptions]}
                value={depLocalId}
                onChange={setDepLocalId}
                data-testid="task-modal-dependency"
              />
              {depLocalId && (
                <Select
                  label="Modo da dependência"
                  options={MODE_OPTIONS}
                  value={depMode}
                  onChange={(v) => setDepMode(v as DependencyMode)}
                  data-testid="task-modal-dependency-mode"
                />
              )}
            </div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} data-testid="task-modal-cancel">
          Cancelar
        </Button>
        <Button icon="check" disabled={!canAdd} onClick={handleAdd} data-testid="task-modal-submit">
          Adicionar tarefa
        </Button>
      </ModalFooter>
    </Modal>
  );
};

TaskFormModal.displayName = 'TaskFormModal';
